// inputSnapshot.js
//
// Turns the Data Fitting page's inputs into plain JSON and back.
//
// WHY THIS IS NOT JUST JSON.stringify(state)
// ────────────────────────────────────────────────────────────────────────
// The page keeps the uploaded data as a real `File` object (`file`, and
// `run.file` inside `globalRuns` / `aaGlobalRuns`), and `excludedRows` is a
// `Set`. JSON.stringify turns a File into `{}` and a Set into `{}` — so a
// "saved" run would reopen with no data and no excluded rows. Here:
//   • File/Blob  →  { __file: { name, type, text } }   (the CSV text itself)
//   • Set        →  { __set: [...] }
// and deserializeInputs() rebuilds real File / Set objects.
//
// Used by:
//   • buildSaveFitPayload  → `input_snapshot` stored with every saved run
//   • useInputSession      → autosave of the live form, restored on refresh
//   • "Reopen" in Run History (via fitRecordStore.stageRestore)

export const SNAPSHOT_VERSION = 1;

// A CSV bigger than this is not stored (the run is still saved; reopening it
// just won't have the data attached). Real kinetics CSVs are a few KB.
export const MAX_FILE_CHARS = 2000000;

const isBlobLike = (v) => typeof Blob !== 'undefined' && v instanceof Blob;

async function toJsonSafe(value, warnings, depth) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (depth > 12) return undefined;

  if (value instanceof Date) return value.toISOString();

  if (isBlobLike(value)) {
    const name = value.name || 'data.csv';
    const type = value.type || 'text/csv';
    const text = await value.text();
    if (text.length > MAX_FILE_CHARS) {
      warnings.push(`"${name}" is too large to store with the run (${Math.round(text.length / 1000)} KB).`);
      return { __file: { name, type, text: null, tooLarge: true } };
    }
    return { __file: { name, type, text } };
  }

  if (value instanceof Set) {
    const items = [];
    for (const v of value) items.push(await toJsonSafe(v, warnings, depth + 1));
    return { __set: items };
  }

  if (Array.isArray(value)) {
    const out = [];
    for (const v of value) {
      const safe = await toJsonSafe(v, warnings, depth + 1);
      out.push(safe === undefined ? null : safe);
    }
    return out;
  }

  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const safe = await toJsonSafe(v, warnings, depth + 1);
    if (safe !== undefined) out[k] = safe;
  }
  return out;
}

// values:  { fieldName: currentValue, ... }
// options: { skip: [fieldNames left out], transform: { fieldName: fn(value) } }
// returns: { snapshot: { version, saved_at, fields }, warnings: [] }
export async function serializeInputs(values, { skip = [], transform = {} } = {}) {
  const warnings = [];
  const fields = {};
  for (const [key, raw] of Object.entries(values || {})) {
    if (skip.includes(key)) continue;
    const value = transform[key] ? transform[key](raw) : raw;
    const safe = await toJsonSafe(value, warnings, 0);
    if (safe !== undefined) fields[key] = safe;
  }
  return {
    snapshot: { version: SNAPSHOT_VERSION, saved_at: new Date().toISOString(), fields },
    warnings,
  };
}

function revive(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(revive);
  if (value.__file) {
    const { name, type, text } = value.__file;
    if (text == null || typeof File === 'undefined') return null;
    return new File([text], name, { type: type || 'text/csv' });
  }
  if (value.__set) return new Set(value.__set.map(revive));
  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = revive(v);
  return out;
}

// snapshot → { fieldName: value } with real File / Set objects rebuilt.
// Tolerates null / malformed snapshots (returns {}), and snapshots written by
// a newer version (unknown fields are simply ignored by the caller's setters).
export function deserializeInputs(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || !snapshot.fields) return {};
  return revive(snapshot.fields);
}

// The component registry is refilled from the database on every page load, so
// a snapshot only stores the USER-DEFINED entries (source === 'user'); storing
// the whole catalogue would add megabytes to every save.
export const onlyUserComponents = (list) =>
  (Array.isArray(list) ? list : []).filter((c) => c && c.source === 'user');

// Merge restored user components into whatever the catalogue load already put
// there — never overwrite the DB entries.
export function mergeUserComponents(current, restored) {
  const base = Array.isArray(current) ? current : [];
  const have = new Set(base.map((c) => String(c && c.id).toUpperCase()));
  const extra = (Array.isArray(restored) ? restored : [])
    .filter((c) => c && !have.has(String(c.id).toUpperCase()));
  return extra.length ? [...base, ...extra] : base;
}
